import { Component, Input, OnInit, ChangeDetectorRef } from '@angular/core';
import { FadeIn, LeaveToLeft } from '../animations';
import { Banner, BannerService } from '@uzurv/clara';
import { isNullOrUndefined, BannerConstants, CacheKeyConstants } from '@uzurv/core';

/**
 * @whatItDoes - Displays relevant banners from an input array and rotates them. The user can dismiss the banners,
 * or if it is a version update banner, the user can click to refresh the app, clearing the cache. Dismissed banners
 * are tracked in localStorate.
 *
 * @howItWorks - Use the `banners` input to asynchronously feed the component an `Array<Banner>` collection. The input
 * will merge new banners into the local state, or if the input contains a release banner, will substitute the whole
 * collection for that one. If there more that one banner (and no release banner) the component will auto-rotate through
 * the collection
 */
@Component({
  selector: 'app-banner',
  templateUrl: './banner.component.html',
  styleUrls: ['./banner.component.scss'],
  animations: [FadeIn, LeaveToLeft]
})
export class BannerComponent implements OnInit {
  _banners: Array<Banner> = [];

  @Input() set banners(banners: Array<Banner>) {
    if (!isNullOrUndefined(banners)) {
      const releaseBanner = banners.find(banner => banner.bannerType.code === BannerConstants.BANNER_TYPE_CODE_APP_RELEASE);
      if (!isNullOrUndefined(releaseBanner)) {
        this.rotate = false;
        this._banners = [releaseBanner];
      } else {
        this.rotate = true;
        banners.forEach(banner => {
          if (isNullOrUndefined(this._banners.find(item => item.id === banner.id))) {
            this._banners.push(banner);
          }
        });
      }
    }
  }

  rotate = true;

  index = 0;

  leaving = false;

  constructor(private bannerService: BannerService) {}

  ngOnInit() {
    setInterval(() => this.rotateBanners(), 5000);
  }

  getSeverityIconString(banner: Banner): string {
    switch (banner.bannerSeverity.code) {
      case BannerConstants.BANNER_SEVERITY_CODE_IMPORTANT:
        return 'warning';
      case BannerConstants.BANNER_SEVERITY_CODE_CRITICAL:
        return 'error';
      default:
        return null;
    }
  }

  shouldShowRefreshButton(banner: Banner): boolean {
    return banner.bannerType.code === BannerConstants.BANNER_TYPE_CODE_APP_RELEASE;
  }

  // TODO: we can save tokens and open tabs
  refresh(): void {
    const dismissedBanners: { number: Array<number> } = isNullOrUndefined(localStorage.getItem(CacheKeyConstants.DISMISSED_BANNERS))
      ? {}
      : JSON.parse(localStorage.getItem(CacheKeyConstants.DISMISSED_BANNERS));
    localStorage.clear();
    localStorage.setItem(CacheKeyConstants.DISMISSED_BANNERS, JSON.stringify(dismissedBanners));
    window.location.reload();
  }

  dismissBanner(banner: Banner): void {
    this.leaving = true;
    // Wait for animation
    setTimeout(() => {
      this.delayedDismissBanner(banner);
    }, 250);
    this.index = 0;
  }

  private rotateBanners() {
    if (this.rotate) {
      if (!isNullOrUndefined(this._banners) && this.index < this._banners.length - 1) {
        this.index++;
      } else {
        this.index = 0;
      }
    }
  }

  private delayedDismissBanner(banner: Banner) {
    this.bannerService.dismissBanner(banner);
    this._banners.splice(this._banners.findIndex(item => item.id === banner.id));
    this.leaving = false;
  }
}
